import React, { useContext } from "react";
import { alertSuccess } from "../../utils/alerts";
import withFormState from "Components/withFormState";
import Select from "Components/select/Select";
import Toggle from "Components/toggle/Toggle";
import Button from "Components/button/Button";
import { formatServerErrors } from "Utils/serverFormats";
import { convertFileToBase64 } from "Utils/file";
import ReactDropzone from "react-dropzone";
import CustomerSelect from "Components/customerSelect/CustomerSelect";
import TranslatedString from "Components/TranslatedString";
import t from "Utils/translation";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { Icon, upload, file } from "Icons/index";
import s from "./newImports.scss";

import cx from "classnames";
import { AuthContext } from "../../App";
import { useApiMutation } from "../../hooks";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const formInitialState = {
  type: "domains",
  file: "",
  expert_mode: false,
  skip_invalid_resources: false,
  customer_code: null,
};

const ImportsNewScreen = (props) => {
  const { formData, formErrors } = props;
  const { getLoggedUser, isLoggedUserAdmin, isLoggedUserTrial } = useContext(AuthContext);

  const loggedUser = getLoggedUser();
  const history = useHistory();

  const { mutate: createImport } = useApiMutation("createImport");

  const getTypesForUser = (user) => {
    switch (user.type) {
      case "TeamMember": {
        const types = [];
        if (user.permissions.includes("create_domains")) {
          types.push(
            { value: "domains", label: t("select.value.domains") },
            { value: "alias_domains", label: t("select.value.aliasDomains") },
            { value: "alias_email_accounts", label: t("select.value.aliasEmails") }
          );
        }
        if (user.permissions.includes("create_email_accounts")) {
          types.push({ value: "email_accounts", label: t("select.value.emailAccounts") });
        }
        return types;
      }

      case "Postmaster":
        return [
          { value: "email_accounts", label: t("select.value.emailAccounts") },
          { value: "alias_email_accounts", label: t("select.value.aliasEmails") },
        ];

      default:
        return [
          { value: "domains", label: t("select.value.domains") },
          { value: "email_accounts", label: t("select.value.emailAccounts") },
          { value: "alias_domains", label: t("select.value.aliasDomains") },
          { value: "alias_email_accounts", label: t("select.value.aliasEmails") },
        ];
    }
  };

  const types = getTypesForUser(loggedUser);

  const validate = (values) => {
    let errors = {};
    if (!values.file) {
      errors.file = t("errorMsg.required");
      window.alert(t("alert.missingFile"));
    }

    if (isLoggedUserAdmin() && !values.customer_code) {
      errors.customer_code = t("errorMsg.required");
    }

    return errors;
  };

  const hasExpertMode = () => {
    if (["alias_domains", "alias_email_accounts"].includes(formData.type)) return false;
    // trial has never expert mode
    if (isLoggedUserTrial) return false;

    // admin has always expert mode
    if (isLoggedUserAdmin()) return true;

    const loggedUser = getLoggedUser();
    if (loggedUser.is_reseller || loggedUser.is_customer_reseller) return true;

    // vianova postmaster has always expert mode
    if (loggedUser.type === "Postmaster" && loggedUser.is_vianova) return true;

    return false;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    const { formData, setFormLoading, unsetFormLoading, setFormErrors } = props;
    setFormLoading(true);

    const errors = validate(formData);

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      unsetFormLoading();
      return;
    }

    try {
      const { file, ...otherData } = formData;
      const content = await convertFileToBase64(file);

      const payload = Object.assign(otherData, {
        csv_file_content: content,
        csv_file_name: file.name,
        csv_file_size: file.size,
      });

      await createImport(payload);
      alertSuccess(t("alert.importCreatedSuccess"));
      resetAndGoBack();
    } catch (error) {
      const serverErrors = await formatServerErrors(error);
      props.setFormErrors(serverErrors);
    } finally {
      props.unsetFormLoading();
    }
  };

  const onDrop = (files, reject) => {
    if (files.length > 1 || reject.length > 0) {
      window.alert(t("alert.invalidFileCSVFormat"));
    } else {
      props.setFormFieldValue({ file: files[0] });
    }
  };

  const resetAndGoBack = () => {
    props.resetForm();

    history.push("/imports");
  };

  const resetFileField = () => {
    props.setFormFieldError("file", null);
    props.setFormFieldValue({ file: undefined });
  };

  const resetExpertModeAndSetValue = (value) => {
    props.setFormFieldValue({ ...value, expert_mode: false });
  };

  const isVianovaPostmaster = loggedUser.type === "Postmaster" && loggedUser.is_vianova;
  const isReseller = loggedUser.is_reseller || loggedUser.is_customer_reseller;

  return (
    <div className="formsPage">
      <div className={cx("sectionTitle", "titleFP")}>{t("imports.add.title")}</div>

      <div className="wrapScrollFP">
        <div className="infoFP">
          <div className="sectionDesc">{t("imports.add.desc")}</div>
          <div className="wrapBtnFP">
            <Button className="btnDocsFP" docsKey="imports_add" color="docs" sizeSmall>
              button.docs
            </Button>
            <div className="btnFileFP">
              <a href={`${BASE_URL_PUBLIC_ASSETS}/Imports-examples.zip`} download>
                {t("action.download.example.csv")}
              </a>
            </div>
          </div>
        </div>

        <form className="wrapFormsFP" method="post" onSubmit={submitForm}>
          <div className="formsFP">
            <Select
              className="inputFull2"
              components={{ DropdownIndicator }}
              clearable={false}
              options={types}
              name="type"
              value={formData.type}
              label={t("input.label.import.type")}
              required
              onChange={resetExpertModeAndSetValue}
            />

            {isLoggedUserAdmin() && (
              <CustomerSelect
                className={cx("inputFull2", "inputAddMargin")}
                label={t("input.label.customer")}
                value={formData.customer_code}
                errors={formErrors.customer_code}
                onChange={(v) => {
                  props.setFormFieldValue({ customer_code: v?.value });
                  props.setFormErrors({ ...formErrors, customer_code: null });
                }}
              />
            )}

            {!isReseller && !isLoggedUserAdmin() && !isVianovaPostmaster && (
              <div className={cx("inputFull2")}>
                <WarningBanner msg={<TranslatedString>infoMsg.expertModeOnlyReseller</TranslatedString>} />
              </div>
            )}

            {isLoggedUserAdmin() && (
              <Toggle
                className={cx("inputFull2", "inputAddMargin")}
                name="skip_invalid_resources"
                checked={formData.skip_invalid_resources}
                onChange={props.setFormFieldValue}
                label="Skip invalid resources"
              />
            )}

            <Toggle
              className={cx("inputFull2", "inputAddMargin")}
              name="expert_mode"
              checked={formData.expert_mode}
              onChange={props.setFormFieldValue}
              label={<TranslatedString>input.label.advanced.csv</TranslatedString>}
              disabled={!hasExpertMode()}
            />
            <div className={cx("wrapDropzone", "inputFull2")}>
              <label className="labelDZ">{t("input.label.import.selectFile")}</label>
              {formData.file || formErrors.file ? (
                <FilePreview formData={formData} formErrors={formErrors} resetFileField={resetFileField} />
              ) : (
                <Dropzone onDrop={onDrop} />
              )}{" "}
            </div>
          </div>
          <div className="footerFP">
            <Button color="text" onClick={resetAndGoBack}>
              button.cancel
            </Button>
            <Button color="green" type="submit" loading={props.isFormLoading}>
              button.import
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Dropzone = ({ onDrop }) => {
  const acceptedMIMETypes = ["text/plain", "application/vnd.ms-excel", "text/x-csv", "text/csv"];
  return (
    <ReactDropzone onDrop={onDrop} accept={acceptedMIMETypes}>
      {(params) => {
        return (
          <div
            {...params.getRootProps()}
            className={cx(
              "py-10 flex items-center justify-center rounded bg-[--bg] cursor-pointer hover:bg-[--bg-d5] transition-all",
              {
                active: params.isDragActive,
                reject: params.isDragReject,
              }
            )}
          >
            <input {...params.getInputProps()} />
            <Icon className="iconDZ" svg={upload} width="1rem" height="1rem" />
            <div className="textDZ">{t("input.label.dragHere")}</div>
          </div>
        );
      }}
    </ReactDropzone>
  );
};
const FilePreview = ({ formErrors, formData, resetFileField }) => {
  return (
    <div
      className={cx("py-10 flex gap-4 flex-col items-center justify-center rounded bg-[--accent-l35]", "inputFull2", {
        withError: formErrors.file,
      })}
    >
      {formErrors.file ? (
        <div className={"errorDZ"}>
          {/* <Icon className="iconDZ" color="red" svg={file} width="3rem" height="3rem" /> */}

          <h3 className={s.invalidFileLabel}>{t("qbmove.file_errors")}</h3>
          <div className={cx("sectionDesc")}>{formErrors.file}</div>
        </div>
      ) : (
        <div className="flex items-center gap-1 max-w-[70%]">
          <Icon className="iconDZ" svg={file} width="1rem" height="1rem" />
          <div className="textDZ">{formData.file.name}</div>
        </div>
      )}

      <Button className="btnDZ" color="outline" onClick={resetFileField} sizeSmall>
        button.change
      </Button>
    </div>
  );
};

export default withFormState(ImportsNewScreen, formInitialState);
